import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { safeHttpUrl } from '../../lib/safeUrl';

import {
  createTaxonomy,
  deleteTaxonomy,
  listTaxonomy,
  mergeTaxonomy,
  updateTaxonomy,
  uploadImageToR2,
  type TaxonomyItem,
  type TaxonomyKind,
  type TaxonomyUpsert,
} from '../../lib/panelApi';
import { imageUrl } from '../../lib/images';

const KIND_LABEL: Record<TaxonomyKind, { single: string; plural: string; emoji: string }> = {
  authors:    { single: 'Author',    plural: 'Authors',    emoji: '✍️' },
  categories: { single: 'Category',  plural: 'Categories', emoji: '🏷️' },
  publishers: { single: 'Publisher', plural: 'Publishers', emoji: '🏢' },
  tags:       { single: 'Tag',       plural: 'Tags',       emoji: '#️⃣' },
};

const KINDS_WITH_WEBSITE: TaxonomyKind[] = ['publishers'];

type Props = {
  accessToken: string;
};

/**
 * Catalogue taxonomy CRUD — one tabbed page covering all four
 * dimensions because the workflow (create / rename / merge / delete)
 * is identical for every kind. Keeping them in one screen makes
 * cross-dimension cleanup (e.g. merging an author named "biography"
 * that's secretly a category) one tab-click away.
 */
type DrawerState =
  | { mode: 'create' }
  | { mode: 'edit'; item: TaxonomyItem }
  | null;

export function TaxonomyPage({ accessToken }: Props) {
  const [kind, setKind] = useState<TaxonomyKind>('authors');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'name' | 'book_count' | 'created_at'>('name');
  const [items, setItems] = useState<TaxonomyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const [drawer, setDrawer] = useState<DrawerState>(null);

  const [mergeSrc, setMergeSrc] = useState<TaxonomyItem | null>(null);
  const [mergeDstUUID, setMergeDstUUID] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listTaxonomy(accessToken, kind, { q, sort, limit: 200, offset: 0 });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [accessToken, kind, q, sort]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = async (item: TaxonomyItem) => {
    const force = item.book_count > 0;
    const confirmMsg = force
      ? `"${item.name}" is referenced by ${item.book_count} book(s). Detach and delete?`
      : `Delete "${item.name}"?`;
    if (!window.confirm(confirmMsg)) return;
    setError(null);
    try {
      await deleteTaxonomy(accessToken, kind, item.uuid, force);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleMerge = async () => {
    if (!mergeSrc || !mergeDstUUID) return;
    setError(null);
    try {
      await mergeTaxonomy(accessToken, kind, mergeSrc.uuid, mergeDstUUID);
      setMergeSrc(null);
      setMergeDstUUID('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
    }
  };

  const meta = KIND_LABEL[kind];

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 14 }}>
        <div>
          <div className="pageTitle">Taxonomy</div>
          <div className="pageSubtitle">
            Manage authors, categories, publishers, and tags — each with a cover image, description
            {kind === 'publishers' ? ', and website' : ''}. Click a card to edit.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'create' })}>
          + New {meta.single.toLowerCase()}
        </button>
      </div>

      {/* Tabs */}
      <div className="row" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {(Object.keys(KIND_LABEL) as TaxonomyKind[]).map((k) => {
          const active = k === kind;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderRadius: 999,
                border: active ? '1px solid rgba(10,25,47,0.9)' : '1px solid rgba(10,25,47,0.12)',
                background: active ? 'rgba(10,25,47,0.92)' : 'rgba(255,255,255,0.6)',
                color: active ? '#fff' : 'rgba(10,25,47,0.7)',
                fontWeight: 800,
                fontSize: 13,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ fontSize: 15 }}>{KIND_LABEL[k].emoji}</span>
              {KIND_LABEL[k].plural}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 999,
                  background: active ? 'rgba(255,255,255,0.18)' : 'rgba(10,25,47,0.08)',
                }}
              >
                {active ? total : ''}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="row" style={{ alignItems: 'end', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div className="field" style={{ marginTop: 0, flex: '1 1 240px' }}>
          <div className="label">Search</div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${meta.plural.toLowerCase()}…`}
          />
        </div>
        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Sort</div>
          <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
            <option value="name">Name</option>
            <option value="book_count">Most books</option>
            <option value="created_at">Newest</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={() => void refresh()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error ? <div className="errorBox" style={{ marginBottom: 12 }}>{error}</div> : null}

      {/* Card grid */}
      {loading ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="item"
              style={{ height: 132, opacity: 0.5, animation: 'pulse 1.4s ease-in-out infinite' }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="item"
          style={{ textAlign: 'center', padding: '40px 16px' }}
        >
          <div style={{ fontSize: 34 }}>{meta.emoji}</div>
          <div style={{ fontWeight: 900, marginTop: 8 }}>No {meta.plural.toLowerCase()} yet</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Create your first {meta.single.toLowerCase()} to get started.
          </div>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => setDrawer({ mode: 'create' })}>
            + New {meta.single.toLowerCase()}
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {items.map((item) => (
            <TaxonomyCard
              key={item.uuid}
              item={item}
              onEdit={() => setDrawer({ mode: 'edit', item })}
              onMerge={() => {
                setMergeSrc(item);
                setMergeDstUUID('');
              }}
              onDelete={() => void handleDelete(item)}
            />
          ))}
        </div>
      )}

      {/* Edit / create drawer */}
      {drawer ? (
        <EditDrawer
          accessToken={accessToken}
          kind={kind}
          initial={drawer.mode === 'edit' ? drawer.item : null}
          onClose={() => setDrawer(null)}
          onSaved={async () => {
            setDrawer(null);
            await refresh();
          }}
        />
      ) : null}

      {/* Merge modal */}
      {mergeSrc ? (
        <MergeModal
          src={mergeSrc}
          candidates={items.filter((c) => c.uuid !== mergeSrc.uuid)}
          dstUUID={mergeDstUUID}
          setDstUUID={setMergeDstUUID}
          onClose={() => setMergeSrc(null)}
          onConfirm={() => void handleMerge()}
        />
      ) : null}
    </div>
  );
}

/* ───── Card ───── */

function TaxonomyCard({
  item,
  onEdit,
  onMerge,
  onDelete,
}: {
  item: TaxonomyItem;
  onEdit: () => void;
  onMerge: () => void;
  onDelete: () => void;
}) {
  const thumb = imageUrl(item.image_filename, 'card');
  const initial = item.name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className="item"
      style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <button
        type="button"
        onClick={onEdit}
        title="Edit"
        style={{
          display: 'block',
          width: '100%',
          aspectRatio: '16 / 9',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          background: thumb
            ? `center / cover no-repeat url("${thumb}")`
            : 'linear-gradient(135deg, rgba(10,25,47,0.06), rgba(10,25,47,0.14))',
        }}
      >
        {!thumb ? (
          <span
            style={{
              display: 'grid',
              placeItems: 'center',
              height: '100%',
              fontSize: 40,
              fontWeight: 900,
              color: 'rgba(10,25,47,0.35)',
            }}
          >
            {initial}
          </span>
        ) : null}
      </button>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={onEdit}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              textAlign: 'left',
              fontWeight: 850,
              fontSize: 15,
              cursor: 'pointer',
              wordBreak: 'break-word',
              color: 'inherit',
            }}
          >
            {item.name}
          </button>
          <span className="pill" style={{ flexShrink: 0 }}>
            <span>{item.book_count}</span>
            <span className="muted">book{item.book_count === 1 ? '' : 's'}</span>
          </span>
        </div>

        {item.description ? (
          <div
            className="muted"
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.description}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
            No description
          </div>
        )}

        {safeHttpUrl(item.website_url) ? (
          <a
            href={safeHttpUrl(item.website_url)!}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 12, fontWeight: 700, wordBreak: 'break-all' }}
          >
            {prettyUrl(item.website_url)} ↗
          </a>
        ) : null}

        <div style={{ flex: 1 }} />

        <div className="row" style={{ gap: 6, marginTop: 4 }}>
          <button className="btn" style={{ flex: 1, fontSize: 12, padding: '6px 8px' }} onClick={onEdit}>
            Edit
          </button>
          <button className="btn" style={{ fontSize: 12, padding: '6px 8px' }} onClick={onMerge}>
            Merge…
          </button>
          <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 8px' }} onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───── Edit / create drawer ───── */

function EditDrawer({
  accessToken,
  kind,
  initial,
  onClose,
  onSaved,
}: {
  accessToken: string;
  kind: TaxonomyKind;
  initial: TaxonomyItem | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const meta = KIND_LABEL[kind];
  const hasWebsite = KINDS_WITH_WEBSITE.includes(kind);
  const isEdit = !!initial;

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initial?.website_url ?? '');
  const [imageFilename, setImageFilename] = useState(initial?.image_filename ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setErr('Name is required');
      return;
    }
    setSaving(true);
    setErr(null);
    const payload: TaxonomyUpsert = {
      name: trimmed,
      image_filename: imageFilename,
      description: description.trim(),
    };
    if (hasWebsite) payload.website_url = websiteUrl.trim();
    try {
      if (isEdit && initial) {
        await updateTaxonomy(accessToken, kind, initial.uuid, payload);
      } else {
        await createTaxonomy(accessToken, kind, payload);
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,28,0.45)', zIndex: 60 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(460px, 96vw)',
          background: 'var(--panel, #fff)',
          boxShadow: '-18px 0 60px rgba(0,0,0,0.28)',
          zIndex: 70,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideIn 0.22s cubic-bezier(.2,.8,.2,1)',
        }}
      >
        <div
          style={{
            padding: '16px 18px',
            borderBottom: '1px solid rgba(10,25,47,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontWeight: 900, fontSize: 16 }}>
              {isEdit ? `Edit ${meta.single.toLowerCase()}` : `New ${meta.single.toLowerCase()}`}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {meta.emoji} {meta.plural}
            </div>
          </div>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          <ImageUploadField
            accessToken={accessToken}
            value={imageFilename}
            onChange={setImageFilename}
            onError={setErr}
          />

          <div className="field">
            <div className="label">Name *</div>
            <input
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              placeholder={`${meta.single} name`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) void save();
              }}
            />
          </div>

          <div className="field">
            <div className="label">Description</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder={`A short bio or summary for this ${meta.single.toLowerCase()}…`}
              style={{ resize: 'vertical', minHeight: 90 }}
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {description.trim().length}/4000
            </div>
          </div>

          {hasWebsite ? (
            <div className="field">
              <div className="label">Website</div>
              <input
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://example.com"
                inputMode="url"
              />
            </div>
          ) : null}

          {isEdit && initial ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {initial.slug ? `/${initial.slug}` : 'no slug'} • {initial.book_count} book
              {initial.book_count === 1 ? '' : 's'} • added {new Date(initial.created_at).toLocaleDateString()}
            </div>
          ) : null}

          {err ? <div className="errorBox" style={{ marginTop: 12 }}>{err}</div> : null}
        </div>

        <div
          style={{
            padding: '14px 18px',
            borderTop: '1px solid rgba(10,25,47,0.08)',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ───── Inline image upload (presign → PUT → store key) ───── */

function ImageUploadField({
  accessToken,
  value,
  onChange,
  onError,
}: {
  accessToken: string;
  value: string;
  onChange: (key: string) => void;
  onError: (msg: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [over, setOver] = useState(false);

  const preview = useMemo(() => imageUrl(value, 'detail'), [value]);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/') && !/\.(png|jpe?g|webp|svg)$/i.test(file.name)) {
        onError('Please choose a PNG, JPEG, WebP, or SVG image.');
        return;
      }
      setUploading(true);
      setProgress(0);
      onError(null);
      try {
        const res = await uploadImageToR2(accessToken, file, setProgress);
        onChange(res.key);
      } catch (e) {
        onError(e instanceof Error ? e.message : 'Image upload failed');
      } finally {
        setUploading(false);
      }
    },
    [accessToken, onChange, onError],
  );

  return (
    <div className="field" style={{ marginTop: 0 }}>
      <div className="label">Image</div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />

      {value && preview ? (
        <div
          style={{
            position: 'relative',
            borderRadius: 14,
            overflow: 'hidden',
            aspectRatio: '16 / 9',
            background: `center / cover no-repeat url("${preview}")`,
            border: '1px solid rgba(10,25,47,0.1)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              gap: 6,
              padding: 8,
              background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent 55%)',
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              Replace
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => onChange('')}
              disabled={uploading}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void handleFile(f);
          }}
          style={{
            borderRadius: 14,
            border: `2px dashed ${over ? 'rgba(204,255,0,0.9)' : 'rgba(10,25,47,0.18)'}`,
            background: over ? 'rgba(204,255,0,0.08)' : 'rgba(10,25,47,0.02)',
            aspectRatio: '16 / 9',
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center',
            cursor: uploading ? 'progress' : 'pointer',
            padding: 12,
          }}
        >
          {uploading ? (
            <div style={{ width: '80%' }}>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Uploading… {progress}%</div>
              <div style={{ height: 8, borderRadius: 8, background: 'rgba(10,25,47,0.08)', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    borderRadius: 8,
                    background: progress < 100 ? 'linear-gradient(90deg,#ccff00,#a6cc00)' : '#22c55e',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 30 }}>🖼️</div>
              <div style={{ fontWeight: 800, fontSize: 13, marginTop: 6 }}>Drop an image or click to upload</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                PNG · JPEG · WebP · SVG — stored on Cloudflare R2, auto-named.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function prettyUrl(u: string): string {
  try {
    return new URL(u).host.replace(/^www\./, '');
  } catch {
    return u;
  }
}

function MergeModal({
  src,
  candidates,
  dstUUID,
  setDstUUID,
  onClose,
  onConfirm,
}: {
  src: TaxonomyItem;
  candidates: TaxonomyItem[];
  dstUUID: string;
  setDstUUID: (v: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const dst = candidates.find((c) => c.uuid === dstUUID) ?? null;
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,28,0.4)', zIndex: 60 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--panel, #fff)',
          padding: 18,
          borderRadius: 18,
          boxShadow: '0 18px 60px rgba(0,0,0,0.32)',
          width: 'min(520px, 92vw)',
          zIndex: 70,
        }}
      >
        <div className="sectionTitle" style={{ margin: 0, marginBottom: 8 }}>
          Merge "{src.name}"
        </div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Move every book associated with "{src.name}" to another entry. The source row will be deleted.
          The cached name in `metadata` is rewritten in the same transaction.
        </div>

        <div className="field">
          <div className="label">Merge into</div>
          <select value={dstUUID} onChange={(e) => setDstUUID(e.target.value)}>
            <option value="">— Choose target —</option>
            {candidates.map((c) => (
              <option key={c.uuid} value={c.uuid}>
                {c.name} ({c.book_count})
              </option>
            ))}
          </select>
        </div>

        {dst ? (
          <div className="item" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 850 }}>{dst.name}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Currently linked to {dst.book_count} book{dst.book_count === 1 ? '' : 's'}.
            </div>
          </div>
        ) : null}

        <div className="row" style={{ gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" disabled={!dstUUID} onClick={onConfirm}>
            Confirm merge
          </button>
        </div>
      </div>
    </>
  );
}
